import { keepPreviousData, queryOptions } from '@tanstack/react-query'
import { z } from 'zod'

import { api } from '@/shared/api/http'
import {
  PAGE_DEFAULT_LIMIT,
  type Page,
  type TransferOrderBy,
  type TransferOut,
} from '@/shared/api/types'
import { isMoney, toCents } from '@/shared/money/money'

export const transferKeys = {
  all: () => ['transfers'] as const,
  lists: () => [...transferKeys.all(), 'list'] as const,
  list: (p: TransferListParams) => [...transferKeys.lists(), p] as const,
} as const

export interface TransferListParams {
  /** Casa a conta como ORIGEM ou como DESTINO. */
  readonly bank_account_id?: string
  readonly date_from?: string
  readonly date_to?: string
  /** Casa por substring, sem diferenciar caixa, na descrição. */
  readonly search?: string
  readonly order_by?: TransferOrderBy
  readonly limit?: number
  readonly offset?: number
}

/**
 * Transferências do tenant. Default do backend: `transfer_date` DESCENDENTE
 * (mais recentes primeiro) — por isso não passo order_by aqui.
 */
export const transfersQueryOptions = (p: TransferListParams = {}) =>
  queryOptions({
    queryKey: transferKeys.list(p),
    queryFn: ({ signal }) =>
      api.get<Page<TransferOut>>(
        '/v1/transfers',
        { ...p, limit: p.limit ?? PAGE_DEFAULT_LIMIT },
        signal,
      ),
    placeholderData: keepPreviousData,
  })

/** Mesmo validador de dinheiro do agendamento: formato válido e > 0, tipo string. */
const moneyField = z.string().refine((v) => isMoney(v) && toCents(v) > 0, {
  message: 'validation:amountPositive',
})

/**
 * Formulário de transferência — espelha TransferCreate.
 *
 * A regra "origem ≠ destino" replica o 422 transfer_same_account do backend
 * para feedback antes do round-trip (o servidor continua sendo a autoridade).
 */
export const transferFormSchema = z
  .object({
    fromBankAccountId: z.uuid('validation:required'),
    toBankAccountId: z.uuid('validation:required'),
    amount: moneyField,
    transferDate: z.iso.date('validation:invalidDate'),
    description: z.string().trim().max(500),
  })
  .refine((d) => d.fromBankAccountId !== d.toBankAccountId, {
    path: ['toBankAccountId'],
    message: 'validation:transferSameAccount',
  })

export type TransferFormValues = z.infer<typeof transferFormSchema>

export interface TransferCreateBody {
  readonly from_bank_account_id: string
  readonly to_bank_account_id: string
  readonly amount: string
  readonly transfer_date: string
  readonly description?: string
}

export function toTransferCreateBody(v: TransferFormValues): TransferCreateBody {
  return {
    from_bank_account_id: v.fromBankAccountId,
    to_bank_account_id: v.toBankAccountId,
    amount: v.amount,
    transfer_date: v.transferDate,
    // Vazio vira AUSENTE, não '': o backend distingue "não informado".
    ...(v.description !== '' && { description: v.description }),
  }
}

/**
 * `POST /v1/transfers` — EFEITO FINANCEIRO: exige Idempotency-Key.
 *
 * A chave vem de fora (useIdempotencyKey, escopo de INTENÇÃO) — gerá-la aqui
 * faria cada retry usar chave nova → transferência DUPLICADA. `operationPath`
 * põe a rota na allowlist de idempotência do http.ts. 201 = criou; 200 = replay.
 */
export function createTransfer(
  body: TransferCreateBody,
  idempotencyKey: string,
): Promise<{ data: TransferOut; created: boolean }> {
  return api.post<TransferOut>('/v1/transfers', {
    body,
    idempotencyKey,
    operationPath: '/v1/transfers',
  })
}
